/*
 * Project: bellcloud
 * File: apps/frontend/src/components/ui/common/SummaryCard.tsx
 * Description: Summary card component for displaying multiple labeled values
 */

import React from 'react';
import styled from '@emotion/styled';

interface SummaryCardItem {
  label: string;
  value: number | string;
  unit?: string | null;
  decimal?: number | null;
  state?: string | null;
}

type SummaryCardSize = 'default' | 'compact';

interface SummaryCardProps {
  items: SummaryCardItem[];
  size?: SummaryCardSize;
}

const CardContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  padding: ${({ theme, $compact }) => $compact ? theme.spacing[3] : theme.spacing[6]}px;
  align-items: flex-start;
  gap: ${({ theme, $compact }) => $compact ? theme.spacing[3] : theme.spacing[5]}px;
  align-self: stretch;

  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.header.nested};
`;

const ItemWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme, $compact }) => $compact ? theme.spacing[1] : theme.spacing[2]}px;
  flex: 1 0 0;
  align-self: stretch;
`;

const Label = styled.div<{ $compact?: boolean }>`
  color: ${({ theme }) => theme.colors.text.emphasis};

  font-family: Inter;
  font-size: ${({ theme, $compact }) => $compact ? theme.fontSize.xs : theme.fontSize.sm}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  letter-spacing: -0.12px;
`;

const Value = styled.div<{ $compact?: boolean }>`
  color: ${({ theme }) => theme.colors.text.primary};

  font-size: ${({ theme, $compact }) => $compact ? theme.fontSize.md : theme.fontSize.xl}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.normal};
`;

export const SummaryCard: React.FC<SummaryCardProps> = ({ items, size = 'default' }) => {
  const compact = size === 'compact';
  const formatValue = (value: number | string, decimal?: number | null, unit?: string | null) => {
    if (typeof value === 'string') return value;

    let formattedValue = value.toString();

    if (decimal !== null && decimal !== undefined) {
      formattedValue = value.toFixed(decimal);
    }

    return unit ? `${formattedValue} ${unit}` : formattedValue;
  };

  return (
    <CardContainer $compact={compact}>
      {items.map((item, index) => (
        <ItemWrapper key={index} $compact={compact}>
          <Label $compact={compact}>{item.label}</Label>
          <Value $compact={compact}>{formatValue(item.value, item.decimal, item.unit)}</Value>
        </ItemWrapper>
      ))}
    </CardContainer>
  );
};
